import { z } from "zod";
import type { IMedia } from "../../interfaces";

// Base schema with common fields
const ProductCollectionCommonSchema = z.object({
  name: z.string().min(1, "Product name is required"),
  description: z.string().nullable(),
  tags: z.array(z.string()).optional(),
  status: z.enum(["active", "draft", "archived"]).default("active"),
  uom: z.enum(["pcs", "kg", "gm", "litre", "ml", "dozen", "pack", "box", "bag", "set", "other", "meter"]).optional().nullable(),
  deal_name: z.string().min(1, "Deal name is required"),
  inserted_at: z.string().optional().nullable(),
  updated_at: z.string().optional().nullable(),
  minimum_order_quantity: z.number().min(1, "Minimum order quantity must be at least 1").nullable(),
});

// Product schemas
const baseProductSchema = ProductCollectionCommonSchema.extend({
  collections: z.array(z.number()).min(1, "Atleast one collection is required"),
  available_quantity: z.number().min(0, "Available quantity (Stock) cannot be negative"),
  medias: z.array(z.object({
    id: z.string(),    
  })).min(1, "Atleast one media is required"),
  sku_id: z.string().min(1, "SKU ID (Tally Code) is required"),
  features: z.string().optional().nullable(),
  material: z.string().min(1, "Material is required"),
  rating_stars: z.number().min(0, "Rating stars must be at least 0").max(5, "Rating stars must be at most 5"),
  review_count: z.number().min(0, "Review count must be at least 0"),
  cost_price: z.number().min(1, "Price must be greater than 0"),
  minimum_order_quantity: z.number().min(1, "Minimum order quantity must be at least 1"),
  pdf_catalog_media_id: z.string().optional().nullable(),
});

export const createProductSchema = baseProductSchema

export const editProductSchema = baseProductSchema.extend({
  id: z.number({ required_error: "Product ID is required" }),
});

// Collection schemas
const baseCollectionSchema = ProductCollectionCommonSchema.extend({
  products: z.array(z.number()),
  medias: z.array(z.object({
    id: z.string(),    
  })).min(1, "Media is required"),
  cost_price: z.number().min(1, "Price must be greater than 0").nullable(),
  minimum_order_quantity: z.number().min(1, "Minimum order quantity must be at least 1").nullable(),
});

export const createCollectionSchema = baseCollectionSchema;
export const editCollectionSchema = baseCollectionSchema.extend({
  id: z.number({ required_error: "Collection ID is required" }),
});

// Type inference
export type CreateProductInput = z.infer<typeof createProductSchema>;
export type EditProductInput = z.infer<typeof editProductSchema>;
export type CreateCollectionInput = z.infer<typeof createCollectionSchema>;
export type EditCollectionInput = z.infer<typeof editCollectionSchema>;

// Extended types with IMedia
export type IProduct = Omit<EditProductInput, 'medias' | 'collections' | 'features'> & {
  medias: IMedia[];
  collections: ICollection[];
  features: string[];
  inserted_at: string;
};

export type ICollection = Omit<EditCollectionInput, 'products'> & {
  products: IProduct[];
  medias?: IMedia[];
};

export type FormValuesType<A extends "create" | "edit", E extends "products" | "collections"> = 
  A extends "create" 
    ? (E extends "products" ? CreateProductInput : CreateCollectionInput)
    : (E extends "products" ? EditProductInput : EditCollectionInput);
